import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import DiagramRenderer from "@/components/csat/DiagramRenderer";
import ReportQuestionButton from "@/components/csat/ReportQuestionButton";
import { 
  ArrowRight, CheckCircle, XCircle, Eye, EyeOff, 
  ChevronLeft, ChevronRight, Clock, Target, Brain,
  Calculator, BookOpen, BarChart3, Filter, Zap, RotateCcw,
  Percent, TrendingUp, Train, Ruler, Scale, Divide, Hash,
  Puzzle, Shuffle, Gauge, PieChart, Table, Layers, Coins,
  Users, MapPin, Sigma, Network, Lightbulb, ListOrdered,
  GitBranch, Grid3x3, Boxes, Timer, Type, Languages, AlignLeft
} from "lucide-react";

// Renders each element of a machine arrangement (numbers/words) as its own chip
// so a sequence like "20 68 37 55 36 70" reads as a clean row, not a run-on.
const ArrangementCells = ({ text }) => {
  const cells = (text || "").trim().split(/\s+/).filter(Boolean);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="arrangement-cells">
      {cells.map((c, i) => (
        <span
          key={i}
          className="min-w-[2.25rem] px-2 py-1 rounded-md bg-white border border-slate-300 text-slate-900 font-mono text-[15px] text-center"
        >
          {c}
        </span>
      ))}
    </div>
  );
};

// Parses machine input-output / step-arrangement questions where an "Input: ..."
// is transformed over "Step 1: ... | Step 2: ..." with a trailing question.
const parseMachineSteps = (raw) => {
  const firstStepIdx = raw.search(/Step\s*\d+\s*:/i);
  if (firstStepIdx < 0) return null;

  let intro = raw.slice(0, firstStepIdx).trim();
  const stepsPart = raw.slice(firstStepIdx);

  // Pull the "Input: ..." sequence out of the intro so it can be highlighted.
  let input = "";
  const im = intro.match(/Input\s*:\s*([^.]*)\.?/i);
  if (im) {
    input = im[1].trim();
    intro = (intro.slice(0, im.index) + intro.slice(im.index + im[0].length)).trim();
  }
  intro = intro.replace(/^[\s.|]+/, "").trim();

  const tokens = stepsPart.split(/(Step\s*\d+\s*:)/i).filter((t) => t && t.trim() !== "");
  const steps = [];
  let trailing = "";
  for (let i = 0; i < tokens.length; i += 2) {
    const label = (tokens[i] || "").replace(/\s*:\s*$/, "").trim();
    let body = (tokens[i + 1] || "").replace(/\s*\|\s*$/, "").trim();
    // Peel a trailing directive question off the final step.
    if (i + 2 >= tokens.length) {
      const qm = body.match(/((?:What|Which|How|Find|If|After|Identify|Determine|In\s+which)\b[\s\S]*\?)\s*$/i);
      if (qm) {
        trailing = qm[1].trim();
        body = body.slice(0, qm.index).replace(/\s*\|\s*$/, "").trim();
      }
    }
    if (label) steps.push({ label, body });
  }
  if (steps.length < 2) return null;
  return { intro, input, steps, trailing };
};

// Renders a question body. Handles (a) machine input-output / step-arrangement
// questions and (b) sentence-ordering / para-jumble questions that embed labeled
// sentences like "(Q) ... / (R) ..." — otherwise renders plain text.
const QuestionBody = ({ text }) => {
  const raw = text || "";

  // --- Machine input-output / step arrangement ---
  const stepCount = (raw.match(/Step\s*\d+\s*:/gi) || []).length;
  if (stepCount >= 2) {
    const parsed = parseMachineSteps(raw);
    if (parsed) {
      const { intro, input, steps, trailing } = parsed;
      return (
        <div className="mb-6 space-y-3" data-testid="question-machine">
          {intro && <p className="text-base text-slate-700 leading-relaxed">{intro}</p>}
          {input && (
            <div className="flex items-center gap-3 flex-wrap p-2.5 rounded-xl bg-indigo-50 border border-indigo-200">
              <span className="text-xs font-bold uppercase tracking-wide text-indigo-700 bg-indigo-100 px-2.5 py-1 rounded-md shrink-0">
                Input
              </span>
              <ArrangementCells text={input} />
            </div>
          )}
          <div className="space-y-2">
            {steps.map((s, i) => (
              <div key={i} className="flex items-center gap-3 flex-wrap p-2.5 rounded-xl bg-slate-50 border border-slate-200">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-700 bg-white border border-slate-300 px-2.5 py-1 rounded-md shrink-0">
                  {s.label}
                </span>
                <ArrangementCells text={s.body} />
              </div>
            ))}
          </div>
          {trailing && <p className="text-base font-semibold text-slate-900 leading-relaxed pt-1">{trailing}</p>}
        </div>
      );
    }
  }

  const labelMatches = raw.match(/\([A-Z]\)/g) || [];
  const isJumble = labelMatches.length >= 3 && raw.includes("/");

  if (!isJumble) {
    return <p className="text-lg text-slate-900 mb-6 leading-relaxed whitespace-pre-wrap">{raw}</p>;
  }

  const firstIdx = raw.search(/\([A-Z]\)/);
  const intro = raw.slice(0, firstIdx).trim();
  const afterIntro = raw.slice(firstIdx);
  const parts = afterIntro.split(/\s*\/\s*/);

  const items = [];
  let trailing = "";
  parts.forEach((p, idx) => {
    const m = p.match(/^\(([A-Z])\)\s*([\s\S]*)$/);
    if (!m) return;
    let body = m[2].trim();
    if (idx === parts.length - 1) {
      // Peel a trailing directive question (ends with "?") off the last sentence.
      const t = body.match(/^([\s\S]*?[.!])\s+([\s\S]*\?)\s*$/);
      if (t) { body = t[1].trim(); trailing = t[2].trim(); }
    }
    items.push({ label: m[1], text: body });
  });

  if (items.length < 3) {
    return <p className="text-lg text-slate-900 mb-6 leading-relaxed whitespace-pre-wrap">{raw}</p>;
  }

  return (
    <div className="mb-6 space-y-3" data-testid="question-jumble">
      {intro && <p className="text-base text-slate-700 leading-relaxed">{intro}</p>}
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.label} className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200">
            <span className="w-7 h-7 rounded-lg bg-indigo-100 text-indigo-700 font-bold text-sm flex items-center justify-center shrink-0">
              {it.label}
            </span>
            <span className="text-[15px] text-slate-900 leading-relaxed pt-0.5">{it.text}</span>
          </div>
        ))}
      </div>
      {trailing && <p className="text-base font-semibold text-slate-900 leading-relaxed pt-1">{trailing}</p>}
    </div>
  );
};


const CSATUniversalPractice = ({ isPro, onBack, initialSubTopic = null, exam = "UPSC" }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [freeLimitBlocked, setFreeLimitBlocked] = useState(false);
  
  // Section data
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);

  // Chapter data (chapter-wise practice)
  const [chapterData, setChapterData] = useState(null);
  const [loadingChapters, setLoadingChapters] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [chapterSearch, setChapterSearch] = useState("");
  
  // Practice config
  const [difficulty, setDifficulty] = useState("all");
  const [numQuestions, setNumQuestions] = useState(20);
  const [selectedSubTopic, setSelectedSubTopic] = useState(initialSubTopic);
  const [chapterProgress, setChapterProgress] = useState(null);
  
  // Practice state
  const [sessionId, setSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showSolution, setShowSolution] = useState(false);
  const [results, setResults] = useState(null);
  const [startTime, setStartTime] = useState(null);
  
  // Per-question timing for analytics
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [questionTimes, setQuestionTimes] = useState({});

  // Icons + premium styling for sections
  const sectionMeta = {
    quant:     { icon: Calculator, gradient: "from-blue-500 to-indigo-600",   glow: "shadow-blue-500/30",   label: "Quantitative Aptitude", sub: "Arithmetic • Number sense • Speed maths" },
    reasoning: { icon: Brain,      gradient: "from-violet-500 to-purple-600", glow: "shadow-violet-500/30", label: "Logical Reasoning",     sub: "Puzzles • Series • Analytical ability" },
    verbal:    { icon: BookOpen,   gradient: "from-emerald-500 to-teal-600",  glow: "shadow-emerald-500/30",label: "Verbal Ability",        sub: "Comprehension • Grammar • Vocabulary" },
    di:        { icon: BarChart3,  gradient: "from-amber-500 to-orange-600",  glow: "shadow-amber-500/30",  label: "Data Interpretation",   sub: "Tables • Charts • Caselets" },
    advanced_maths: { icon: Sigma, gradient: "from-rose-500 to-pink-600",    glow: "shadow-rose-500/30",   label: "Advanced Maths",        sub: "Algebra • Geometry • Trigonometry • Mensuration" },
  };

  // Keyword → icon + gradient matcher for chapter cards (falls back to section styling).
  const CHAPTER_ICON_RULES = [
    [/percent/i,                       Percent,     "from-rose-500 to-pink-600"],
    [/profit|loss|discount/i,          TrendingUp,  "from-emerald-500 to-green-600"],
    [/interest|compound|simple int/i,  Coins,       "from-amber-500 to-yellow-600"],
    [/train|speed|distance|boat|stream/i, Train,    "from-sky-500 to-blue-600"],
    [/time.*work|pipe|cistern|efficien/i, Timer,    "from-indigo-500 to-violet-600"],
    [/ratio|proportion|partnership|mixture|allegation/i, Scale, "from-teal-500 to-cyan-600"],
    [/average|mean|median/i,           Sigma,       "from-fuchsia-500 to-purple-600"],
    [/hcf|lcm|divisib|factor|number system|remainder/i, Divide, "from-blue-500 to-indigo-600"],
    [/simplif|approxim|surds|indices|power/i, Hash,  "from-cyan-500 to-sky-600"],
    [/percentage|data suffic/i,        Filter,      "from-orange-500 to-amber-600"],
    [/geometry|mensuration|area|volume/i, Ruler,    "from-lime-500 to-green-600"],
    [/probab|permut|combin/i,          Shuffle,     "from-violet-500 to-fuchsia-600"],
    // Reasoning
    [/analytical|logical reason|critical/i, Brain,  "from-violet-500 to-purple-600"],
    [/input.*output/i,                 GitBranch,   "from-sky-500 to-indigo-600"],
    [/pattern|series|sequence/i,       Network,     "from-pink-500 to-rose-600"],
    [/puzzle|arrangement|seating/i,    Puzzle,      "from-amber-500 to-orange-600"],
    [/figure|non.?verbal|visual/i,     Grid3x3,     "from-teal-500 to-emerald-600"],
    [/decision|situation|statement/i,  Lightbulb,   "from-yellow-500 to-amber-600"],
    [/blood relation|direction/i,      MapPin,      "from-red-500 to-rose-600"],
    [/coding|decoding|order|rank/i,    ListOrdered, "from-blue-500 to-cyan-600"],
    // Verbal
    [/comprehension|passage|reading/i, AlignLeft,   "from-emerald-500 to-teal-600"],
    [/grammar|sentence|error/i,        Type,        "from-indigo-500 to-blue-600"],
    [/vocab|synonym|antonym|word/i,    Languages,   "from-fuchsia-500 to-pink-600"],
    // DI
    [/table/i,                         Table,       "from-amber-500 to-orange-600"],
    [/pie|chart/i,                     PieChart,    "from-rose-500 to-red-600"],
    [/bar|graph|line/i,                BarChart3,   "from-sky-500 to-blue-600"],
    [/caselet|mixed di/i,              Layers,      "from-violet-500 to-purple-600"],
  ];

  const getChapterVisual = (name) => {
    for (const [re, Icon, gradient] of CHAPTER_ICON_RULES) {
      if (re.test(name)) return { Icon, gradient };
    }
    const meta = sectionMeta[selectedSection?.id] || {};
    return { Icon: meta.icon || Boxes, gradient: meta.gradient || "from-slate-500 to-slate-700" };
  };

  // Load sections on mount
  useEffect(() => {
    loadSections();
  }, []);

  // Auto-set section when initialSubTopic is provided (topic-wise practice from Learn mode)
  useEffect(() => {
    if (initialSubTopic && sections.length > 0) {
      // Find which section this sub-topic belongs to
      const sectionMap = {
        // Quantitative topics
        "hcf_lcm": "quant", "divisibility_rules": "quant", "remainders": "quant",
        "simplification_bodmas": "quant", "percentage_basics": "quant", "percentage_successive": "quant",
        "ratio_proportion": "quant", "mixture_alligation": "quant", "profit_loss_basics": "quant",
        "profit_loss_discount": "quant", "simple_interest": "quant", "compound_interest": "quant",
        "time_work_basics": "quant", "time_work_pipes": "quant", "speed_distance_basics": "quant",
        "speed_distance_trains": "quant", "boats_streams": "quant", "average_weighted": "quant",
        "quadratic_equations": "quant", "progressions_ap": "quant", "progressions_gp": "quant",
        "triangles": "quant", "circles": "quant", "area_perimeter_2d": "quant", "volume_surface_area_3d": "quant",
        "probability_basic": "quant", "permutation_combination": "quant",
        // Reasoning topics
        "syllogisms": "reasoning", "analogies_word": "reasoning", "analogies_number": "reasoning",
        "series_number": "reasoning", "series_alphabet": "reasoning", "coding_decoding_letter": "reasoning",
        "blood_relations_direct": "reasoning", "directions_simple": "reasoning",
        "sitting_arrangement_linear": "reasoning", "sitting_arrangement_circular": "reasoning",
        "puzzles_floor": "reasoning", "puzzles_comparison": "reasoning", "input_output_number": "reasoning",
        "statement_assumptions": "reasoning", "statement_conclusions": "reasoning",
        "inequality_coded": "reasoning", "inequality_direct": "reasoning",
        "cause_effect": "reasoning", "assertion_reason": "reasoning",
        "pattern_recognition": "reasoning", "mirror_images": "reasoning", "cube_dice": "reasoning",
        // English topics
        "error_spotting": "verbal", "sentence_improvement": "verbal", "tenses": "verbal",
        "synonyms": "verbal", "antonyms": "verbal", "one_word_substitution": "verbal", "idioms_phrases": "verbal",
        "para_jumbles": "verbal", "cloze_vocabulary": "verbal", "rc_factual": "verbal", "rc_inferential": "verbal",
        // DI topics
        "tables_single": "di", "bar_graphs_simple": "di", "line_graphs_single": "di",
        "pie_charts_single": "di", "caselets": "di", "quantity_comparison": "di"
      };
      
      const sectionId = sectionMap[initialSubTopic] || "quant";
      const section = sections.find(s => s.id === sectionId);
      if (section) {
        setSelectedSection(section);
      }
    }
  }, [initialSubTopic, sections]);

  const loadSections = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${API_BASE}/csat/universal-practice/sections`, {
        params: { exam },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSections(data.sections || []);
    } catch (err) {
      toast({ variant: "destructive", description: "Could not load sections" });
    } finally {
      setLoading(false);
    }
  };

  // Load chapters whenever a section is selected (chapter-wise practice)
  useEffect(() => {
    if (selectedSection && !selectedSubTopic) {
      loadChapters(selectedSection.id);
    }
    // eslint-disable-next-line
  }, [selectedSection]);

  const loadChapters = async (sectionId) => {
    setLoadingChapters(true);
    setChapterData(null);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(
        `${API_BASE}/csat/universal-practice/chapters/${sectionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChapterData(data);
    } catch (err) {
      toast({ variant: "destructive", description: "Could not load chapters" });
    } finally {
      setLoadingChapters(false);
    }
  };

  const startPractice = async (mode = "fresh", redoSessionId = null) => {
    if (!selectedSection && !selectedSubTopic) return;
    
    setLoading(true);
    toast({ description: "🔄 Loading practice questions...", duration: 3000 });
    
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({
        section: selectedSection?.id || "quantitative_aptitude",
        num_questions: numQuestions.toString(),
        mode,
        exam,
      });
      if (redoSessionId) params.append("redo_session_id", redoSessionId);
      
      // Chapter-wise filter (unless "All Chapters (Mixed)" chosen)
      if (selectedChapter && selectedChapter !== "__all__") {
        params.append("chapter", selectedChapter);
      }

      // Add sub_topics filter if specified
      if (selectedSubTopic) {
        params.append("sub_topics", selectedSubTopic);
      }
      
      const { data } = await axios.post(
        `${API_BASE}/csat/universal-practice/start?${params}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSessionId(data.session_id);
      setQuestions(data.questions || []);
      setCurrentQ(0);
      setAnswers({});
      setShowSolution(false);
      setResults(null);
      setStartTime(Date.now());
      setQuestionStartTime(Date.now());
      setQuestionTimes({});
      setChapterProgress({
        total: data.chapter_total || 0,
        practiced: data.practiced || 0,
        fresh_remaining: data.fresh_remaining || 0,
        complete: data.chapter_complete || false,
        mode: data.mode || mode,
      });
      
      toast({ description: `✅ ${data.num_questions} questions loaded!` });
    } catch (err) {
      if (err.response?.status === 403) {
        setFreeLimitBlocked(true);
      }
      toast({ 
        variant: "destructive", 
        description: err.response?.data?.detail || "Could not load questions" 
      });
    } finally {
      setLoading(false);
    }
  };

  const submitPractice = async () => {
    setLoading(true);
    
    // Record time for the current question before submitting
    const finalQuestionTimes = { ...questionTimes };
    if (questions[currentQ]?.id && questionStartTime) {
      const timeSpent = Math.round((Date.now() - questionStartTime) / 1000);
      finalQuestionTimes[questions[currentQ].id] = (finalQuestionTimes[questions[currentQ].id] || 0) + timeSpent;
    }
    
    try {
      const token = localStorage.getItem("token");
      const timeTaken = Math.round((Date.now() - startTime) / 1000);
      
      // Submit to main practice endpoint first
      const { data } = await axios.post(
        `${API_BASE}/csat/universal-practice/submit/${sessionId}`,
        { answers, time_taken: timeTaken },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Build detailed analytics submission
      const attempts = questions.map(q => ({
        question_id: q.id,
        selected_answer: answers[q.id] || null,
        time_spent: finalQuestionTimes[q.id] || 60, // Default 60s if not tracked
        topic: q.topic,
        sub_topic: q.sub_topic,
        difficulty: q.difficulty
      }));
      
      // Submit to analytics engine (non-blocking)
      axios.post(
        `${API_BASE}/universal-analytics/submit-detailed`,
        {
          session_id: sessionId,
          exam_type: "csat",
          section: selectedSection?.id || "quantitative_aptitude",
          attempts: attempts,
          total_time: timeTaken
        },
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch(() => {}); // Silent fail for analytics
      
      setResults(data);
      toast({ 
        description: `🎯 Score: ${data.score}/${data.total} (${data.percentage}%)` 
      });
    } catch (err) {
      toast({ variant: "destructive", description: "Could not submit answers" });
    } finally {
      setLoading(false);
    }
  };
  
  // Track time when switching questions
  const handleQuestionChange = (newIdx) => {
    // Save time for current question
    if (questions[currentQ]?.id && questionStartTime) {
      const timeSpent = Math.round((Date.now() - questionStartTime) / 1000);
      setQuestionTimes(prev => ({
        ...prev,
        [questions[currentQ].id]: (prev[questions[currentQ].id] || 0) + timeSpent
      }));
    }
    
    setCurrentQ(newIdx);
    setShowSolution(false);
    setQuestionStartTime(Date.now());
  };

  const currentQuestion = questions[currentQ];

  // Difficulty badge colors
  const difficultyColors = {
    easy: "bg-green-100 text-green-800 border-green-200",
    medium: "bg-yellow-100 text-yellow-800 border-yellow-200",
    hard: "bg-orange-100 text-orange-800 border-orange-200",
    advanced: "bg-red-100 text-red-800 border-red-200"
  };

  if (freeLimitBlocked && !questions.length) {
    return (
      <div className="min-h-screen bg-slate-50/40 py-10 px-4">
        <div className="max-w-lg mx-auto">
          <Button variant="ghost" onClick={onBack} className="mb-4" data-testid="practice-back-btn">
            <ChevronLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Card className="border-2 border-amber-200 shadow-xl" data-testid="practice-free-limit-card">
            <CardContent className="p-8 text-center space-y-4">
              <div className="mx-auto w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Today's free set is done 🎯</h2>
              <p className="text-slate-600">
                The free plan includes <b>1 practice set (10 questions) per day</b>. Come back
                tomorrow for another free set — or upgrade to <b>PRO</b> for unlimited practice
                across every chapter, right now.
              </p>
              <div className="flex gap-3 justify-center pt-2">
                <Button variant="outline" onClick={onBack} data-testid="practice-limit-back">Maybe later</Button>
                <Button className="bg-gradient-to-r from-amber-500 to-orange-500 text-white" onClick={() => (window.location.href = "/upgrade")} data-testid="practice-limit-upgrade">
                  Upgrade to PRO
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-6 py-12 space-y-8">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button 
            variant="outline" 
            className="border-slate-200" 
            onClick={() => {
              if (questions.length > 0 && !results) {
                if (window.confirm("Exit practice? Your progress will be lost.")) {
                  setQuestions([]);
                  setSelectedChapter(null);
                }
              } else if (selectedChapter && !questions.length) {
                setSelectedChapter(null);
              } else if (selectedSection && !questions.length) {
                setSelectedSection(null);
                setChapterData(null);
              } else {
                onBack();
              }
            }}
          >
            ← Back
          </Button>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="px-4 py-2 text-sm border-emerald-500 text-emerald-600">
              <Zap className="w-3 h-3 mr-1" />
              Universal Bank
            </Badge>
            <Badge 
              variant="outline" 
              className={`px-4 py-2 text-sm ${isPro ? 'border-slate-900 text-slate-900' : 'border-slate-300 text-slate-500'}`}
            >
              {isPro ? "PRO" : "FREE"}
            </Badge>
          </div>
        </div>

        {/* Title */}
        <div className="pb-6 border-b border-slate-100">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Target className="w-6 h-6 text-emerald-600" />
            {selectedSubTopic ? `Practice: ${selectedSubTopic.replace(/_/g, ' ').toUpperCase()}` : "CSAT Practice"}
          </h1>
          <p className="text-slate-500 mt-1">
            {selectedSubTopic 
              ? `Topic-specific MCQs from Universal Bank`
              : "Prelims-standard MCQs • Easy to Advanced difficulty"
            }
          </p>
        </div>

        {/* Section Selection - Skip if topic is pre-selected */}
        {!selectedSection && !selectedSubTopic && (
          <div className="space-y-6">
            <h2 className="text-base font-medium text-slate-700">Select a Section</h2>
            
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mb-3"></div>
                <p className="text-slate-600">Loading sections...</p>
              </div>
            ) : (
              <div className="grid gap-5 md:grid-cols-2">
                {sections.map((section) => {
                  const meta = sectionMeta[section.id] || { icon: Target, gradient: "from-slate-500 to-slate-700", glow: "shadow-slate-500/30", label: section.name, sub: "" };
                  const Icon = meta.icon;
                  return (
                    <Card 
                      key={section.id}
                      className="group cursor-pointer border-slate-200 hover:border-transparent hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden relative"
                      onClick={() => setSelectedSection(section)}
                      data-testid={`section-${section.id}`}
                    >
                      <div className={`absolute inset-0 bg-gradient-to-br ${meta.gradient} opacity-0 group-hover:opacity-[0.04] transition-opacity`} />
                      <CardContent className="p-6 relative">
                        <div className="flex items-center gap-4 mb-4">
                          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${meta.gradient} flex items-center justify-center shadow-lg ${meta.glow} group-hover:scale-110 group-hover:-rotate-3 transition-transform duration-300 shrink-0`}>
                            <Icon className="w-8 h-8 text-white" strokeWidth={2} />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-bold text-slate-900 text-lg leading-tight">
                              {meta.label}
                            </h3>
                            {meta.sub && <p className="text-xs text-slate-500 mt-0.5">{meta.sub}</p>}
                          </div>
                        </div>
                        <div className="flex items-center text-sm font-medium text-slate-500 group-hover:text-slate-900 transition-colors">
                          Start Practice
                          <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Chapter Selection — pick any chapter (grouped by module) */}
        {selectedSection && !selectedChapter && !selectedSubTopic && questions.length === 0 && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 flex-wrap">
              <Button variant="ghost" size="sm" onClick={() => { setSelectedSection(null); setChapterData(null); setChapterSearch(""); }}>
                ← Change Section
              </Button>
              <h2 className="text-xl font-semibold text-slate-900">{selectedSection.name} — Select a Chapter</h2>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
              <input
                type="text"
                value={chapterSearch}
                onChange={(e) => setChapterSearch(e.target.value)}
                placeholder="Search a chapter…"
                className="w-full pl-4 pr-4 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-emerald-400"
                data-testid="chapter-search"
              />
            </div>

            {loadingChapters ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mb-3"></div>
                <p className="text-slate-600">Loading chapters...</p>
              </div>
            ) : (
              <div className="space-y-8">
                {(chapterData?.modules || []).map((mod) => {
                  const q = chapterSearch.trim().toLowerCase();
                  const chs = mod.chapters.filter((c) => !q || c.chapter.toLowerCase().includes(q));
                  if (chs.length === 0) return null;
                  return (
                    <div key={mod.module} data-testid={`chapter-module-${mod.module}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">{mod.module}</h3>
                        <span className="text-xs text-slate-400">({chs.length})</span>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {chs.map((c) => {
                          const { Icon, gradient } = getChapterVisual(c.chapter);
                          const pct = c.count ? Math.min(100, Math.round((c.practiced / c.count) * 100)) : 0;
                          return (
                          <Card
                            key={c.chapter}
                            className="group cursor-pointer border-slate-200 hover:border-transparent hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                            onClick={() => setSelectedChapter(c.chapter)}
                            data-testid={`chapter-${c.chapter}`}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-start gap-3 mb-3">
                                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300 shrink-0`}>
                                  <Icon className="w-5 h-5 text-white" strokeWidth={2.2} />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-start justify-between gap-2">
                                    <h4 className="font-semibold text-slate-900 text-sm leading-snug">{c.chapter}</h4>
                                    {c.complete && (
                                      <Badge className="bg-emerald-100 text-emerald-700 text-[10px] shrink-0" data-testid={`chapter-complete-${c.chapter}`}>✓ Done</Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                                <div
                                  className={`h-full bg-gradient-to-r ${gradient} rounded-full transition-all`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-[11px] text-slate-500" data-testid={`chapter-progress-${c.chapter}`}>
                                  {c.practiced} / {c.count} practiced{c.fresh_remaining > 0 ? ` · ${c.fresh_remaining} fresh` : ""}
                                </span>
                                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-700 group-hover:translate-x-1 transition-all" />
                              </div>
                            </CardContent>
                          </Card>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Configuration (after chapter selection OR when topic is pre-selected, before starting) */}
        {((selectedSection && selectedChapter) || selectedSubTopic) && questions.length === 0 && !loading && (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              {!selectedSubTopic && (
                <Button variant="ghost" size="sm" onClick={() => setSelectedChapter(null)}>
                  ← Change Chapter
                </Button>
              )}
              <h2 className="text-xl font-semibold text-slate-900">
                {selectedSubTopic 
                  ? `Topic: ${selectedSubTopic.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`
                  : (selectedChapter === "__all__" ? `${selectedSection?.name} · All Chapters` : selectedChapter)
                }
              </h2>
              {selectedSubTopic && (
                <Badge className="bg-blue-100 text-blue-800">From Learn Mode</Badge>
              )}
            </div>
            
            <Card className="border-slate-200">
              <CardContent className="p-6 space-y-6">
                {/* Number of Questions */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Number of Questions
                  </label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[10, 20, 30, 50].map((num) => (
                      <button
                        key={num}
                        onClick={() => setNumQuestions(num)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          numQuestions === num
                            ? "bg-emerald-600 text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                        data-testid={`num-questions-${num}`}
                      >
                        {num} Questions
                      </button>
                    ))}
                  </div>
                  {!isPro && numQuestions > 10 && (
                    <p className="text-xs text-amber-600 mt-2">
                      ⚠️ FREE users are limited to 10 questions. Upgrade to PRO for more.
                    </p>
                  )}
                </div>
                
                {/* Start Button */}
                <Button 
                  onClick={() => startPractice("fresh")}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base"
                  data-testid="start-practice-btn"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  Start Practice (fresh questions)
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Loading State */}
        {loading && selectedSection && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mb-4"></div>
            <p className="text-lg font-semibold text-slate-900">Loading Questions</p>
            <p className="text-sm text-slate-500">Preparing exam-standard MCQs...</p>
          </div>
        )}

        {/* Practice Questions */}
        {questions.length > 0 && currentQuestion && (
          <div className="space-y-6">
            {/* Question Navigator */}
            <div className="flex flex-wrap gap-2 justify-center p-4 bg-slate-50 rounded-xl">
              {questions.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuestionChange(idx)}
                  className={`w-9 h-9 rounded-lg text-sm font-bold transition-all ${
                    currentQ === idx
                      ? "bg-emerald-600 text-white ring-2 ring-emerald-300"
                      : answers[questions[idx]?.id]
                        ? results
                          ? results.results?.find(r => r.question_id === questions[idx]?.id)?.is_correct
                            ? "bg-green-200 text-green-800"
                            : "bg-red-200 text-red-800"
                          : "bg-emerald-100 text-emerald-800"
                        : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
                  }`}
                  data-testid={`question-nav-${idx}`}
                >
                  {idx + 1}
                </button>
              ))}
            </div>

            {/* Question Card — keyed by question id so it fully remounts on
                navigation, preventing Google Translate stale option text. */}
            <Card key={currentQuestion.id} className="border-emerald-100 shadow-sm">
              <CardContent className="p-6">
                {/* Question Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Badge className="bg-emerald-100 text-emerald-800">
                      Q {currentQ + 1} / {questions.length}
                    </Badge>
                    <Badge 
                      variant="outline" 
                      className={difficultyColors[currentQuestion.difficulty] || "border-slate-200"}
                    >
                      {currentQuestion.difficulty?.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {startTime && !results && (
                      <div className="flex items-center gap-1 text-sm text-slate-500">
                        <Clock className="w-4 h-4" />
                        {Math.round((Date.now() - startTime) / 60000)} min
                      </div>
                    )}
                    <ReportQuestionButton
                      mcqId={currentQuestion.id}
                      questionText={currentQuestion.question}
                    />
                  </div>
                </div>

                {/* Topic Tag */}
                <div className="text-xs text-slate-500 mb-3">
                  Topic: {currentQuestion.sub_topic?.replace(/_/g, ' ').toUpperCase()}
                </div>

                {/* Diagram (charts / tables / seating / non-verbal) */}
                {currentQuestion.diagram && (
                  <DiagramRenderer diagram={currentQuestion.diagram} reveal={showSolution || !!results} />
                )}

                {/* Question Text */}
                <QuestionBody text={currentQuestion.question} />

                {/* Options */}
                <div className="space-y-3">
                  {currentQuestion.options?.map((option) => {
                    const isSelected = answers[currentQuestion.id] === option.key;
                    const resultItem = results?.results?.find(r => r.question_id === currentQuestion.id);
                    const isCorrect = resultItem?.correct_answer === option.key;
                    const showResult = results || showSolution;

                    return (
                      <button
                        key={option.key}
                        onClick={() => {
                          if (!results) {
                            setAnswers({ ...answers, [currentQuestion.id]: option.key });
                          }
                        }}
                        disabled={!!results}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                          showResult && isCorrect
                            ? "border-green-500 bg-green-50"
                            : showResult && isSelected && !isCorrect
                              ? "border-red-500 bg-red-50"
                              : isSelected
                                ? "border-emerald-500 bg-emerald-50"
                                : "border-slate-200 hover:border-emerald-300 hover:bg-slate-50"
                        }`}
                        data-testid={`option-${option.key}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-3">
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              showResult && isCorrect
                                ? "bg-green-500 text-white"
                                : showResult && isSelected && !isCorrect
                                  ? "bg-red-500 text-white"
                                  : isSelected
                                    ? "bg-emerald-500 text-white"
                                    : "bg-slate-100 text-slate-600"
                            }`}>
                              {option.key.toUpperCase()}
                            </span>
                            <span className="text-slate-700">{option.text}</span>
                          </span>
                          {showResult && isCorrect && <CheckCircle className="w-5 h-5 text-green-600" />}
                          {showResult && isSelected && !isCorrect && <XCircle className="w-5 h-5 text-red-600" />}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Solution Toggle */}
                {answers[currentQuestion.id] && !results && (
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setShowSolution(!showSolution)}
                  >
                    {showSolution ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                    {showSolution ? "Hide" : "Show"} Solution
                  </Button>
                )}

                {/* Explanation — full detailed solution */}
                {(showSolution || results) && (() => {
                  const r = results?.results?.find(x => x.question_id === currentQuestion.id) || {};
                  const steps = (r.solution_steps && r.solution_steps.length)
                    ? r.solution_steps
                    : (r.explanation ? r.explanation.split("\n").filter(Boolean) : []);
                  return (
                    <div className="mt-4 space-y-3" data-testid="explanation-block">
                      {r.given && (
                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200" data-testid="explanation-given">
                          <p className="text-xs font-semibold text-slate-600 mb-1">📋 GIVEN</p>
                          <p className="text-sm text-slate-800 whitespace-pre-wrap">{r.given}</p>
                        </div>
                      )}

                      <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                        <p className="text-xs font-semibold text-blue-700 mb-2">SOLUTION</p>
                        {steps.length > 0 ? (
                          <ol className="list-decimal list-inside space-y-1 text-sm text-slate-800">
                            {steps.map((s, i) => (
                              <li key={i} className="whitespace-pre-wrap">{s.replace(/^\d+[\.\)]\s*/, "")}</li>
                            ))}
                          </ol>
                        ) : (
                          <p className="text-sm text-slate-800">Solution will be shown after submission.</p>
                        )}
                      </div>

                      {r.formula_used && (
                        <div className="p-3 bg-indigo-50 rounded-xl border border-indigo-200" data-testid="explanation-formula">
                          <p className="text-xs font-semibold text-indigo-700 mb-1">FORMULA USED</p>
                          <p className="text-sm text-slate-800 font-mono whitespace-pre-wrap">{r.formula_used}</p>
                        </div>
                      )}

                      {r.shortcut_method && (
                        <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200" data-testid="explanation-shortcut">
                          <p className="text-xs font-semibold text-emerald-700 mb-1">⚡ SMART APPROACH / SHORTCUT</p>
                          <p className="text-sm text-slate-800 whitespace-pre-wrap">{r.shortcut_method}</p>
                        </div>
                      )}

                      {r.verification && (
                        <div className="p-3 bg-teal-50 rounded-xl border border-teal-200" data-testid="explanation-verification">
                          <p className="text-xs font-semibold text-teal-700 mb-1">✅ VERIFY YOUR ANSWER</p>
                          <p className="text-sm text-slate-800 whitespace-pre-wrap">{r.verification}</p>
                        </div>
                      )}

                      {r.common_mistake && (
                        <div className="p-3 bg-amber-50 rounded-xl border border-amber-200" data-testid="explanation-mistake">
                          <p className="text-xs font-semibold text-amber-700 mb-1">⚠️ COMMON MISTAKE TO AVOID</p>
                          <p className="text-sm text-slate-800 whitespace-pre-wrap">{r.common_mistake}</p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Navigation & Submit */}
            <div className="flex justify-between items-center">
              <Button
                variant="outline"
                onClick={() => handleQuestionChange(Math.max(0, currentQ - 1))}
                disabled={currentQ === 0}
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>

              {!results && (
                <Button
                  onClick={submitPractice}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={Object.keys(answers).length < questions.length}
                  data-testid="submit-practice-btn"
                >
                  Submit ({Object.keys(answers).length}/{questions.length})
                </Button>
              )}

              {results && (
                <div className="text-center px-6 py-3 rounded-xl bg-emerald-50">
                  <p className="text-2xl font-bold text-emerald-900">
                    {results.score}/{results.total}
                  </p>
                  <p className="text-sm text-emerald-600">{results.percentage}% Accuracy</p>
                </div>
              )}

              <Button
                variant="outline"
                onClick={() => handleQuestionChange(Math.min(questions.length - 1, currentQ + 1))}
                disabled={currentQ === questions.length - 1}
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Results Summary */}
            {results && (
              <Card className="border-slate-200 bg-slate-50">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Practice Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-white rounded-xl">
                      <p className="text-2xl font-bold text-emerald-600">{results.score}</p>
                      <p className="text-xs text-slate-500">Correct</p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-xl">
                      <p className="text-2xl font-bold text-red-600">{results.total - results.score}</p>
                      <p className="text-xs text-slate-500">Incorrect</p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-xl">
                      <p className="text-2xl font-bold text-blue-600">{results.percentage}%</p>
                      <p className="text-xs text-slate-500">Accuracy</p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-xl">
                      <p className="text-2xl font-bold text-slate-600">
                        {Math.round(results.time_taken / 60)} min
                      </p>
                      <p className="text-xs text-slate-500">Time Taken</p>
                    </div>
                  </div>
                  
                  {results.weak_topics?.length > 0 && (
                    <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                      <p className="text-sm font-medium text-amber-800 mb-2">Topics to Improve:</p>
                      <div className="flex flex-wrap gap-2">
                        {results.weak_topics.map((topic, idx) => (
                          <Badge key={idx} variant="outline" className="border-amber-300 text-amber-700">
                            {topic.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-4 space-y-2">
                    <Button
                      variant="outline"
                      className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => startPractice(chapterProgress?.mode || "fresh", sessionId)}
                      data-testid="redo-set-btn"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Redo this set (same {questions.length} questions)
                    </Button>
                    {selectedChapter && selectedChapter !== "__all__" && !chapterProgress?.complete && (
                      <Button
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => startPractice("fresh")}
                        data-testid="next-set-btn"
                      >
                        <ArrowRight className="w-4 h-4 mr-2" />
                        Continue chapter · Next {numQuestions} fresh questions
                      </Button>
                    )}
                    {selectedChapter && selectedChapter !== "__all__" && chapterProgress?.complete && (
                      <>
                        <div className="text-center p-3 rounded-lg bg-emerald-50 text-emerald-700 text-sm font-medium" data-testid="chapter-done-banner">
                          🎉 You've practiced every question in {selectedChapter}!
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" className="flex-1" onClick={() => startPractice("review_all")} data-testid="review-all-btn">
                            Review all
                          </Button>
                          <Button variant="outline" className="flex-1" onClick={() => startPractice("review_wrong")} data-testid="review-wrong-btn">
                            Review my mistakes
                          </Button>
                        </div>
                      </>
                    )}
                    <Button
                      variant="ghost"
                      className="w-full"
                      onClick={() => {
                        setQuestions([]);
                        setResults(null);
                        setChapterProgress(null);
                        setSelectedChapter(null);
                        if (selectedSection) loadChapters(selectedSection.id);
                      }}
                      data-testid="back-to-chapters-btn"
                    >
                      ← Back to chapters
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CSATUniversalPractice;
